import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../models/response-models/user';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { UserSubscription } from '../models/response-models/subscription';
import { Email } from '../models/response-models/organisation/email';
import { GeneralFunctionsService } from './general-functions.service';
import { Phone } from '../models/response-models/organisation/phone';
import { ConfirmationResponse } from '../models/response-models/confirmation-response';
import { CodeValidatorView } from '../code-validator/code-validator.component';
import { Permission } from '../models/response-models/role';
import { UserAttendance, ShiftData } from '../models/response-models/user-attendance';
import { MultiSelectionItem } from '../models/application-models/multi-selection-item';
import { LoginResponse } from '../models/response-models/login-response';
import { CodeValidatorV2View } from '../models/application-models/code-validator-v2-view';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
    private http: HttpClient,
    private generalFunctionsService: GeneralFunctionsService,
  ) { }

  dateRangeForm: UntypedFormGroup = new UntypedFormGroup({
    dateRange: new UntypedFormControl()
  });

  getUsers(): Observable<User[]> {
    return this.http.get<User[]>(environment.serverUrl + "/users");
  }

  getUsersByOrganisationID(orgID: string): Observable<User[]> {
    return this.http.get<User[]>(environment.serverUrl + `/users/org/${orgID}`);
  }

  getAllUsers(): Observable<User[]> {
    return this.http.get<User[]>(environment.serverUrl + "/users/all");
  }

  getUserByID(userID: string): Observable<User> {
    return this.http.get<User>(environment.serverUrl + `/users/${userID}`);
  }

  getAllUserAttendance(startDate: string, endDate: string): Observable<UserAttendance[]> {
    return this.http.get<any[]>(environment.serverUrl + `/users/UserTimeAndAttendance?startDate=` + startDate + `&endDate=` + endDate);
  }

  getUserAttendanceById(userId: string, startDate: string, endDate: string): Observable<UserAttendance> {
    return this.http.get<UserAttendance>(environment.serverUrl + `/users/UserTimeAndAttendance/` + userId + `?startDate=` + startDate + `&endDate=` + endDate);
  }
  getUserShiftById(userId: string, shiftId: string): Observable<ShiftData> {
    return this.http.get<ShiftData>(environment.serverUrl + `/users/UserTimeAndAttendance/` + userId + `/` + shiftId);
  }

 switchOrganisation(orgID: string, currentOrganisationID: string): Observable<HttpResponse<LoginResponse>> {
  const options = {
    refreshToken: localStorage.refreshToken,
    organisationId: orgID,
    CurrentOrganisationID: currentOrganisationID
  }
     return this.http.post<LoginResponse>(environment.serverUrl + `/users/switchOrganisation`,  options, {observe: 'response'} );
  }

  confirmUser(userID: string) {
    return this.http.get<User>(environment.serverUrl + `/users/${userID}/confirmation`);
  }

  getNextFreePin(): Observable<string> {
    return this.http.get<string>(environment.serverUrl + `/users/nextFreePin`);
  }

  getUserCredentials(userID: string): Observable<{ username: string; pin: string }> {
    return this.http.get<{ username: string; pin: string }>(environment.serverUrl + `/users/${userID}/credentials`);
  }

  createUser(form: UntypedFormGroup): Observable<User> {
    const userID: string = form.controls.id.value;
    const email: Email = {
      id: this.generalFunctionsService.generateUUID(),
      type: "Email",
      name: form.controls.name.value,
      address: form.controls.email.value
    }

    const phone: Phone = {
      id: this.generalFunctionsService.generateUUID(),
      type: "Phone",
      name: form.controls.name.value,
      number: form.controls.mobile.value
    }

    const options = {
      eMail: email,
      employeeID: form.controls.employeeID.value,
      friendlyName: form.controls.friendlyName.value ? form.controls.friendlyName.value : form.controls.name.value,
      id: userID,
      isExcludedFromShift: form.controls.isExcludedFromShift.value,
      name: form.controls.name.value,
      phone: phone,
      isActive: true,
      managerEmail: form.controls.managerEmail.value,
      managerMobile: form.controls.managerMobile.value,
      useManagerTwoFactorAuthentication: form.controls.useManagerTwoFactorAuthentication.value
    }
    return this.http.post<User>(environment.serverUrl + `/users`, options);
  }

  updateUser(form: UntypedFormGroup, user: User): Observable<User> {
    const userID: string = form.controls.id.value;
    let userEmailID: string;
    let userPhoneID: string;

    if (user && user.eMail) {
      userEmailID = user.eMail.id;
    } else {
      userEmailID = this.generalFunctionsService.generateUUID();
    }

    if (user && user.phone) {
      userPhoneID = user.phone.id;
    } else {
      userPhoneID = this.generalFunctionsService.generateUUID();
    }

    const email: Email = {
      id: userEmailID,
      type: "Email",
      name: form.controls.name.value,
      address: form.controls.email.value
    }

    const phone: Phone = {
      id: userPhoneID,
      type: "Phone",
      name: form.controls.name.value,
      number: form.controls.mobile.value
    }

    const options = {
      eMail: email,
      employeeID: form.controls.employeeID.value,
      friendlyName: form.controls.friendlyName.value ? form.controls.friendlyName.value : form.controls.name.value,
      id: userID,
      isExcludedFromShift: form.controls.isExcludedFromShift.value,
      name: form.controls.name.value,
      phone: phone
    }
    return this.http.put<User>(environment.serverUrl + `/users`, options);
  }

  createUserV2(form: UntypedFormGroup): Observable<User> {
    const userID: string = form.controls.id.value;
    const email: Email = {
      id: this.generalFunctionsService.generateUUID(),
      type: "Email",
      name: form.controls.name.value,
      address: form.controls.email.value
    }

    const options = {
      eMail: email,
      employeeID: form.controls.employeeID.value,
      friendlyName: form.controls.friendlyName.value ? form.controls.friendlyName.value : form.controls.name.value,
      id: userID,
      isExcludedFromShift: form.controls.isExcludedFromShift.value,
      name: form.controls.name.value,
      pin: form.controls.pin.value,
      username: form.controls.username.value,
      isActive: true,
      managerEmail: form.controls.managerEmail.value,
      useManagerTwoFactorAuthentication: form.controls.useManagerTwoFactorAuthentication.value,
      // Sent at creation so the backend can skip MFA for POS-only users with no Hub access.
      permissionSetIDs: form.controls.role.value ? [form.controls.role.value.id] : []
    }
    return this.http.post<User>(environment.serverUrl + `/users/v2`, options);
  }

  isLinkedAccount(username: string, email: string): Observable<HttpResponse<{ canLink: boolean }>> {
    const options: any = {
      username: username,
      email: email
    }

    return this.http.post<{ canLink: boolean }>(environment.serverUrl + `/users/isLinkedAccount`, options, { observe: 'response' });
  }

  createLinkedUserV2(form: UntypedFormGroup): Observable<User> {
    const userID: string = form.controls.id.value;
    const email: Email = {
      id: this.generalFunctionsService.generateUUID(),
      type: "Email",
      name: form.controls.name.value,
      address: form.controls.email.value
    }

    const options = {
      eMail: email,
      employeeID: form.controls.employeeID.value,
      friendlyName: form.controls.friendlyName.value ? form.controls.friendlyName.value : form.controls.name.value,
      id: userID,
      isExcludedFromShift: form.controls.isExcludedFromShift.value,
      name: form.controls.name.value,
      pin: form.controls.pin.value,
      username: form.controls.username.value,
      isActive: true,
      managerEmail: form.controls.managerEmail.value,
      useManagerTwoFactorAuthentication: form.controls.useManagerTwoFactorAuthentication.value,
      // Sent at creation so the backend can skip MFA for POS-only users with no Hub access.
      permissionSetIDs: form.controls.role.value ? [form.controls.role.value.id] : []
    }
    return this.http.post<User>(environment.serverUrl + `/users/v2/linked`, options);
  }

  updateUserV2(form: UntypedFormGroup, user: User): Observable<User> {
    const userID: string = form.controls.id.value;
    let userEmailID: string;

    if (user && user.eMail) {
      userEmailID = user.eMail.id;
    } else {
      userEmailID = this.generalFunctionsService.generateUUID();
    }

    const email: Email = {
      id: userEmailID,
      type: "Email",
      name: form.controls.name.value,
      address: form.controls.email.value
    }

    const options = {
      eMail: email,
      employeeID: form.controls.employeeID.value,
      friendlyName: form.controls.friendlyName.value ? form.controls.friendlyName.value : form.controls.name.value,
      id: userID,
      isExcludedFromShift: form.controls.isExcludedFromShift.value,
      name: form.controls.name.value,
      pin: form.controls.pin.value,
      username: form.controls.username.value
    }
    return this.http.put<User>(environment.serverUrl + `/users/v2`, options);
  }

  resetMFA(userID: string) {
        const options: any = {
        id: userID
    }
    return this.http.post<any>(environment.serverUrl + `/users/resetMFA`, options);
  }
  removeUserByID(userID: string) {
    const options: any = {
      body: {
        id: userID
      }
    }

    return this.http.delete<any>(environment.serverUrl + `/users`, options);
  }

  getSubscriptions(): Observable<UserSubscription[]> {
    return this.http.get<UserSubscription[]>(environment.serverUrl + "/subscriptions/users");
  }

  // Starts the MFA process (send email code / provision app-authenticator secret) WITHOUT activating
  // the account. Returns { message } = "MFA_CODE_SENT" for email, or the otpauth:// URI for app MFA.
  startMfaV2(userID: string, organisationID: string, validationCode: string): Observable<HttpResponse<{ message: string }>> {
    const options: any = {
      userID: userID,
      organisationID: organisationID,
      validationCode: validationCode
    }

    return this.http.post<{ message: string }>(environment.serverUrl + `/users/v2/signup/start-mfa`, options, { observe: 'response' });
  }

  // Resends the email MFA code during a v2 signup that has not been activated yet. Authorised by the
  // invite's validation code — the login-time resendEmailMfaCode cannot be used here because the
  // password is only stored once v2/signup activates the account.
  resendMfaCodeV2(userID: string, organisationID: string, validationCode: string): Observable<HttpResponse<{ message: string }>> {
    const options: any = {
      userID: userID,
      organisationID: organisationID,
      validationCode: validationCode
    }

    return this.http.post<{ message: string }>(environment.serverUrl + `/users/v2/signup/resend-mfa`, options, { observe: 'response' });
  }

  // Activates the account after any MFA has been verified. mfaCode is required only when the
  // organisation has MFA enabled. Returns the PIN/name to reveal once activation succeeds.
  signUpV2(userID: string, organisationID: string, password: string, validationCode: string, mfaCode = ''): Observable<HttpResponse<{ message: string; pin: string; name: string }>> {
    const options: any = {
      userID: userID,
      organisationID: organisationID,
      password: password,
      validationCode: validationCode,
      mfaCode: mfaCode
    }

    return this.http.post<{ message: string; pin: string; name: string }>(environment.serverUrl + `/users/v2/signup`, options, { observe: 'response' });
  }

  validate(form: UntypedFormGroup): Observable<any> {
    const options: any = {
      userID: form.controls.id.value,
      validationCode: form.controls.validationCode.value
    }

    return this.http.post<any>(environment.serverUrl + `/users/validate`, options);
  }

  signUp(form: UntypedFormGroup, validationForm: UntypedFormGroup): Observable<HttpResponse<any>> {
    const options: any = {
      checkUnique: validationForm.controls.checkUnique.value,
      organisationID: form.controls.organisationID.value,
      userID: validationForm.controls.id.value,
      username: form.controls.username.value,
      password: form.controls.password.value,
      validationCode: validationForm.controls.validationCode.value
    }

    return this.http.post<HttpResponse<any>>(environment.serverUrl + `/users/signup`, options, { observe: 'response' });
  }

  revealPin(request: any): Observable<any> {
    return this.http.post<any>(environment.serverUrl + `/users/reveal-pin`, request);
  }

  validateTwoFactorSetup(form: UntypedFormGroup, organisationID: string): Observable<any> {
    const options: any = {
      userID: form.controls.id.value,
      organisationID: organisationID,
      mfaCode: form.controls.validationCode.value
    }

    return this.http.post<any>(environment.serverUrl + `/users/validateTwoFactorSetup`, options);
  }

  // Verifies an existing account's password. The validationCode from the invite token gates the
  // check (valid for 15 minutes) so the endpoint can authorise it without consuming the code.
  checkPassword(userID: string, organisationID: string, password: string, validationCode: string): Observable<boolean> {
    const options = { userID, organisationID, password, validationCode };
    return this.http
      .post<{ isPasswordMatch: boolean }>(environment.serverUrl + `/users/v2/linked/check-password`, options)
      .pipe(map(response => response.isPasswordMatch));
  }

  resendActivationCode(userID: string): Observable<any> {
    return this.http.post<any>(environment.serverUrl + `/users/${userID}/resendCode`, null);
  }

  resendEmailMfaCode(userCreationForm: UntypedFormGroup, organisationID: string) {
     const options: any = {
      username: userCreationForm.controls.username.value,
      password: userCreationForm.controls.password.value
    };
     return this.http.post(environment.serverUrl + '/users/resendMfaCode/' + organisationID, options);

  }

  resendSignUpLink(userID: string): Observable<any> {
    return this.http.post<any>(environment.serverUrl + `/users/${userID}/resendLink`, null);
  }

  resendSignUpLinkV2(userID: string): Observable<any> {
    return this.http.post<any>(environment.serverUrl + `/users/v2/${userID}/resendLink`, null);
  }

  resetPassword(userID: string, form: UntypedFormGroup, fromCC: boolean = false): Observable<any> {
    let options: any = form.value;

    return this.http.post<any>(environment.serverUrl + `/users/${userID}/changePassword/${fromCC}`, options);
  }

  resetForgottenPassword(form: UntypedFormGroup): Observable<any> {
    let options: any = form.value;

    return this.http.post<any>(environment.serverUrl + `/users/login/resetForgottenPassword`, options);
  }

  resetForgottenPasswordV2(form: UntypedFormGroup): Observable<any> {
    let options: any = form.value;

    return this.http.post<any>(environment.serverUrl + `/users/v2/login/resetForgottenPassword`, options);
  }

  sendPasswordResetEmail(form: UntypedFormGroup): Observable<any> {
    let options: any = form.value;

    return this.http.post<any>(environment.serverUrl + `/users/login/forgotPasswordEmail`, options);
  }

  sendPasswordResetEmailByUserName(form: UntypedFormGroup): Observable<any> {
    let options: any = form.value;

    return this.http.post<any>(environment.serverUrl + `/users/login/forgotPassword`, options);
  }

  sendPasswordResetEmailByUserNameV2(form: UntypedFormGroup): Observable<any> {
    let options: any = form.value;

    return this.http.post<any>(environment.serverUrl + `/users/v2/login/forgotPassword`, options);
  }

  resetPIN(userID: string): Observable<any> {
    return this.http.get<any>(environment.serverUrl + `/users/${userID}/changePIN`);
  }

  createLicense(userID: string, subscriptionID: string): Observable<any> {
    let options = [
      {
        id: userID
      }
    ];

    return this.http.post<any>(environment.serverUrl + `/subscriptions/${subscriptionID}/licenses`, options);
  }

  updateLicense(userID: string, subscriptionID: string): Observable<any> {
    let options = [
      {
        id: userID
      }
    ];

    return this.http.put<any>(environment.serverUrl + `/subscriptions/${subscriptionID}/licenses`, options);
  }

  getConfirmation(username: string): Observable<ConfirmationResponse[]> {
    return this.http.get<ConfirmationResponse[]>(environment.serverUrl + `/users/${username}/confirmation`);
  }

  validateAgain(userID: string, request: CodeValidatorView): Observable<any> {
    return this.http.post<any>(environment.serverUrl + `/users/${userID}/confirmation`, request);
  }

  validateAgainV2(userID: string, request: CodeValidatorV2View): Observable<any> {
    return this.http.post<any>(environment.serverUrl + `/users/${userID}/confirmation`, request);
  }

  addPermissionSet(userID: string, permissionSet: Permission) {
    let justID: any = { id: permissionSet.id }

    return this.http.post<any>(environment.serverUrl + `/users/${userID}/permissionSets`, justID);
  }

  removePermissionSet(userID: string, permissionSet: Permission) {
    const options: any = {
      body: permissionSet
    }

    return this.http.delete<any>(environment.serverUrl + `/users/${userID}/permissionSets`, options);
  }

  getUsernameByUserId(userID: string) {
    return this.http.get<string>(environment.serverUrl + `/users/username/${userID}`);
  }

  updateUserShiftData(userId: string, shift: ShiftData) {
    return this.http.put<any>(environment.serverUrl + `/users/UserTimeAndAttendance/${userId}/${shift.shiftId}`, shift);
  }

  deleteUserShiftData(userId: string, shiftId: string) {
    return this.http.delete<any>(environment.serverUrl + `/users/UserTimeAndAttendance/${userId}/${shiftId}`);
  }
  createShiftData(userId: string, shift: ShiftData) {
    return this.http.post<any>(environment.serverUrl + `/users/UserTimeAndAttendance/${userId}`, shift);
  }

  addSiteRelationship(userID: string, siteItem: MultiSelectionItem) {
    let options: any = {
      id: siteItem.id
    }

    return this.http.post<any>(environment.serverUrl + `/users/${userID}/sites`, options);
  }

  removeSiteRelationship(userID: string, siteItem: MultiSelectionItem) {
    let options: any = {
      body: {
        id: siteItem.id
      }
    };

    return this.http.delete<any>(environment.serverUrl + `/users/${userID}/sites`, options);
  }

  verifyAppOtp(userId: string, organisationID: string, mfaCode: string) {
    let options: any = {
      userID: userId,
      organisationID: organisationID,
      mfaCode: mfaCode
    }
    return this.http.post(environment.serverUrl + '/users/validateTwoFactorSetup', options);
  }
}
